import { useCallback, useEffect, useRef, useState } from 'react';

import { noInternetMsg, serverMsg } from '../../../config/messageconstant';
import { getCurrentDate } from '../../../utils/dateFormat';
import {
  BLOCK_STATUS,
  createInitialLoadingState,
  createSettledLoadingState,
  revealSectionBlocks,
  updateBlockStatus,
  VAN_DASHBOARD_LOADING_CONFIG,
} from '../components/loading';
import {
  buildChallanStatus,
  buildDriverKpis,
  buildDriverProgress,
  buildDriverUtilization,
  buildPendingSettlements,
  buildPendingTransfers,
  buildRecentChallans,
  buildSettlementKpis,
  buildStockTransferFunnel,
  buildSummaryKpis,
  INITIAL_DASHBOARD_DATA,
} from '../data/mockData';

const SECTION_FETCHERS = [
  {
    section: 'summary',
    blocks: VAN_DASHBOARD_LOADING_CONFIG.summary,
    applyData: (response) => ({
      summary: buildSummaryKpis(response),
    }),
  },
  {
    section: 'challan',
    blocks: VAN_DASHBOARD_LOADING_CONFIG.challan,
    applyData: (response) => {
      const { challanStatus, daySummary, recentChallans } = response;
      return {
        challanStatus: buildChallanStatus(challanStatus, daySummary),
        recentChallans: buildRecentChallans(recentChallans),
      };
    },
  },
  {
    section: 'driver',
    blocks: VAN_DASHBOARD_LOADING_CONFIG.driver,
    applyData: (response) => {
      const { driverSummary, driverList } = response;
      return {
        driverKpis: buildDriverKpis(driverSummary),
        driverUtilization: buildDriverUtilization(driverList),
        driverProgress: buildDriverProgress(driverList),
      };
    },
  },
  {
    section: 'stockTransfer',
    blocks: VAN_DASHBOARD_LOADING_CONFIG.stockTransfer,
    applyData: (response) => {
      const { stockSummary, stockList } = response;
      return {
        stockTransferFunnel: buildStockTransferFunnel(stockSummary),
        pendingTransfers: buildPendingTransfers(stockList),
      };
    },
  },
  {
    section: 'settlement',
    blocks: VAN_DASHBOARD_LOADING_CONFIG.settlement,
    applyData: (response) => {
      const { settlementSummary, settlementList } = response;
      return {
        settlementKpis: buildSettlementKpis(settlementSummary),
        pendingSettlements: buildPendingSettlements(settlementList),
      };
    },
  },
];

export function useDashboardData({
  showNotification,
  getVanDashboardSummary,
  getVanDashboardChallanManagement,
  getDashboardDriverManagement,
  getDashboardStockManagement,
  getVanDashboardChallanSettlement,
  selectedDate,
} = {}) {
  const [dashboardData, setDashboardData] = useState(INITIAL_DASHBOARD_DATA);
  const [loadingState, setLoadingState] = useState(() =>
    createInitialLoadingState(VAN_DASHBOARD_LOADING_CONFIG),
  );
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchDashboardData = useCallback(async () => {
    if (
      !getVanDashboardSummary ||
      !getVanDashboardChallanManagement ||
      !getDashboardDriverManagement ||
      !getDashboardStockManagement ||
      !getVanDashboardChallanSettlement
    ) {
      return;
    }

    if (!navigator.onLine) {
      showNotification?.({ msg: noInternetMsg, severity: 'error' });
      return;
    }

    const fetchId = fetchIdRef.current + 1;
    fetchIdRef.current = fetchId;
    const isCancelled = () => fetchIdRef.current !== fetchId;

    const date = selectedDate || getCurrentDate();
    const apiCalls = [
      getVanDashboardSummary,
      getVanDashboardChallanManagement,
      getDashboardDriverManagement,
      getDashboardStockManagement,
      getVanDashboardChallanSettlement,
    ];

    setError(null);
    setLoadingState(createInitialLoadingState(VAN_DASHBOARD_LOADING_CONFIG));

    const setBlockStatus = (section, block, status) => {
      if (isCancelled()) {
        return;
      }
      setLoadingState((prev) => updateBlockStatus(prev, section, block, status));
    };

    let hasError = false;

    const fetchSection = async ({ section, blocks, applyData }, apiCall) => {
      if (isCancelled()) {
        return;
      }

      setBlockStatus(section, blocks[0], BLOCK_STATUS.LOADING);

      try {
        const result = await apiCall({ date });

        if (isCancelled()) {
          return;
        }

        if (result.success) {
          setDashboardData((prev) => ({
            ...prev,
            ...applyData(result.response),
          }));
        } else {
          hasError = true;
        }
      } catch {
        hasError = true;
      }

      if (!isCancelled()) {
        await revealSectionBlocks({
          section,
          blocks,
          setStatus: setBlockStatus,
          isCancelled,
        });
      }
    };

    try {
      for (let index = 0; index < SECTION_FETCHERS.length; index += 1) {
        if (isCancelled()) {
          return;
        }
        await fetchSection(SECTION_FETCHERS[index], apiCalls[index]);
      }

      if (hasError && !isCancelled()) {
        setError(new Error(serverMsg));
        showNotification?.({ msg: serverMsg, severity: 'error' });
      }
    } catch (fetchError) {
      if (!isCancelled()) {
        setError(fetchError);
        showNotification?.({ msg: serverMsg, severity: 'error' });
        setLoadingState(createSettledLoadingState(VAN_DASHBOARD_LOADING_CONFIG));
      }
    }
  }, [
    getVanDashboardSummary,
    getVanDashboardChallanManagement,
    getDashboardDriverManagement,
    getDashboardStockManagement,
    getVanDashboardChallanSettlement,
    showNotification,
    selectedDate,
  ]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    dashboardData,
    loadingState,
    error,
    refetch: fetchDashboardData,
  };
}
